<!-- Layer modal — single shell drives both Add Layer and Edit Layer. Mode
     swaps the header copy, the save-button label, the tracker dropdown's
     "required" rule, and which styles section appears: a read-only preview
     of the 13 default styles in add mode, or an editable list (with per-row
     edit/delete + Add Style) in edit mode. -->
<div
  *ngIf="open && form() as f"
  class="fixed inset-0 flex items-center justify-center bg-main-background bg-opacity-75 z-30 p-4">
  <form
    (ngSubmit)="onSubmit()"
    class="bg-white/95 shadow-xl border border-gray-200 rounded-md w-full max-w-xl max-h-[90vh] flex flex-col">
    <div
      class="flex items-center justify-between bg-indigo-500 text-white px-5 py-3 rounded-t-md shrink-0"
      [class.bg-blue-500]="mode === 'edit'"
      [class.bg-indigo-500]="mode === 'add'">
      <p class="oswald-font text-lg font-medium">
        <ng-container *ngIf="mode === 'add'">Add Layer</ng-container>
        <ng-container *ngIf="mode === 'edit'">Edit Layer</ng-container>
      </p>
      <button
        type="button"
        (click)="onClose()"
        [disabled]="saving"
        class="text-white/80 hover:text-white disabled:opacity-60">
        <lucide-icon [img]="icons.X" [size]="18"></lucide-icon>
      </button>
    </div>

    <div class="flex-1 min-h-0 overflow-auto px-5 py-4 flex flex-col gap-4">
      <div>
        <label class="block mb-1 text-xs font-bold text-gray-900">
          Layer Name <span class="text-red-500">*</span>
        </label>
        <input
          type="text"
          [ngModel]="f.name"
          (ngModelChange)="updateField('name', $event)"
          (blur)="markTouched('name')"
          name="layerName"
          [disabled]="saving"
          [class.border-red-400]="showError('name', !f.name.trim())"
          [class.border-gray-300]="!showError('name', !f.name.trim())"
          class="bg-gray-50 border text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60" />
        <p *ngIf="showError('name', !f.name.trim())" class="text-xs text-red-500 mt-1">
          Layer name is required.
        </p>
      </div>

      <div>
        <label class="block mb-1 text-xs font-bold text-gray-900">
          Tracker
          <span *ngIf="mode === 'add'" class="font-normal text-gray-500">(optional)</span>
          <span *ngIf="mode === 'edit'" class="text-red-500">*</span>
        </label>
        <select
          [ngModel]="f.agentId"
          (ngModelChange)="updateField('agentId', $event)"
          (blur)="markTouched('agentId')"
          name="layerAgentId"
          [disabled]="saving || loading"
          [class.border-red-400]="showError('agentId', mode === 'edit' && !f.agentId)"
          [class.border-gray-300]="!showError('agentId', mode === 'edit' && !f.agentId)"
          class="bg-gray-50 border text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60">
          <!-- Add mode: "no tracker yet" is a valid choice. Edit mode:
               keep the placeholder but mark it disabled — matches v2's
               "A device must be selected" guard. -->
          <option
            [ngValue]="''"
            [disabled]="mode === 'edit'">
            <ng-container *ngIf="mode === 'add'">— No tracker (bind later) —</ng-container>
            <ng-container *ngIf="mode === 'edit'">-- Select a tracker --</ng-container>
          </option>
          <option *ngFor="let a of assignableAgents" [ngValue]="a.id">
            {{ a.name || a.id }} ({{ a.id }})
          </option>
        </select>
        <p *ngIf="showError('agentId', mode === 'edit' && !f.agentId)" class="text-xs text-red-500 mt-1">
          A tracker must be selected.
        </p>
        <p *ngIf="mode === 'add'" class="text-xs text-gray-500 mt-1">
          You can leave this empty and bind a tracker later from the layer row.
        </p>
        <p *ngIf="mode === 'edit' && !!f.agentId" class="text-xs text-gray-500 mt-1">
          The dropdown lists trackers not yet bound to a layer, plus the one
          currently assigned to this layer.
        </p>
      </div>

      <div>
        <label class="block mb-1 text-xs font-bold text-gray-900">
          Update Frequency (seconds)
        </label>
        <input
          type="number"
          [ngModel]="f.updateFrequency"
          (ngModelChange)="updateField('updateFrequency', $event)"
          name="layerUpdateFrequency"
          min="1"
          [disabled]="saving"
          class="bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60" />
      </div>

      <!-- ADD mode: read-only preview of the 13 default styles the backend
           will seed. The parent doesn't need to provide anything for this. -->
      <div *ngIf="mode === 'add'">
        <p class="block mb-2 text-xs font-bold text-gray-900">
          Default Styles
          <span class="font-normal text-gray-500">
            ({{ defaultStyles.length }} will be created)
          </span>
        </p>
        <div class="border border-gray-200 rounded-md overflow-hidden">
          <ul class="divide-y divide-gray-100 text-xs">
            <li
              *ngFor="let s of defaultStyles"
              class="flex items-center gap-3 px-3 py-1.5">
              <span
                class="inline-block w-4 h-4 rounded-sm border border-gray-300"
                [style.background-color]="s.fillColor"
                [title]="s.fillColor">
              </span>
              <span class="text-gray-700">{{ s.name }}</span>
              <span class="ml-auto text-gray-400 tabular-nums">{{ s.fillColor }}</span>
            </li>
          </ul>
        </div>
        <p class="text-xs text-gray-500 mt-1">
          Trip-event styles (start, stop, acceleration…) are also pre-wired
          to their tracker codes so live updates render with the correct
          colour.
        </p>
      </div>

      <!-- EDIT mode: editable list of the layer's actual styles + Add Style
           button. Each row has pencil/trash actions. -->
      <div *ngIf="mode === 'edit'">
        <div class="flex items-center justify-between mb-2">
          <p class="block text-xs font-bold text-gray-900">
            Styles
            <span class="font-normal text-gray-500">({{ styles.length }})</span>
          </p>
          <button
            type="button"
            (click)="addStyle.emit()"
            [disabled]="saving"
            class="hidden flex items-center gap-1 px-2 py-1 rounded-md bg-blue-500 hover:bg-blue-600 text-white text-xs oswald-font disabled:opacity-60">
            <lucide-icon [img]="icons.Plus" [size]="12"></lucide-icon>
            Add Style
          </button>
        </div>
        <div
          *ngIf="!loadingStyles && styles.length === 0"
          class="text-xs text-gray-500 italic">
          No styles for this layer.
        </div>
        <div
          *ngIf="styles.length > 0"
          class="border border-gray-200 rounded-md overflow-hidden">
          <ul class="divide-y divide-gray-100 text-xs max-h-56 overflow-y-auto">
            <li
              *ngFor="let s of styles"
              class="flex items-center gap-3 px-3 py-1.5 hover:bg-gray-50">
              <span
                class="inline-block w-4 h-4 rounded-sm border border-gray-300 shrink-0"
                [style.background-color]="s.fillColor || '#000000'"
                [title]="s.fillColor || '—'">
              </span>
              <span class="text-gray-700 truncate">{{ s.name || '—' }}</span>
              <span class="ml-auto text-gray-400 tabular-nums">
                {{ s.fillColor || '—' }}
              </span>
              <button
                type="button"
                (click)="editStyle.emit(s)"
                [disabled]="saving"
                title="Edit style"
                class="text-gray-600 hover:text-indigo-700 disabled:opacity-40">
                <lucide-icon [img]="icons.Pencil" [size]="14"></lucide-icon>
              </button>
              <button
                type="button"
                (click)="deleteStyle.emit(s)"
                [disabled]="saving || isDefaultStyle(s)"
                [title]="isDefaultStyle(s) ? 'Cannot delete the default style' : 'Delete style'"
                class="text-red-600 hover:text-red-800 disabled:opacity-40 disabled:cursor-not-allowed">
                <lucide-icon [img]="icons.Trash2" [size]="14"></lucide-icon>
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div class="flex justify-end gap-3 px-5 py-3 border-t border-gray-200 bg-white rounded-b-md shrink-0">
      <button
        type="button"
        (click)="onClose()"
        [disabled]="saving"
        class="px-5 py-2 rounded-md border border-gray-400 oswald-font disabled:opacity-60">
        Cancel
      </button>
      <button
        type="submit"
        [disabled]="saving || loading || !canSubmit()"
        [ngClass]="mode === 'add'
          ? 'bg-indigo-500 hover:bg-indigo-600'
          : 'bg-blue-500 hover:bg-blue-600'"
        class="px-6 py-2 rounded-md text-white oswald-font disabled:opacity-60 disabled:cursor-not-allowed">
        <ng-container *ngIf="!saving">
          <ng-container *ngIf="mode === 'add'">Create Layer</ng-container>
          <ng-container *ngIf="mode === 'edit'">Save</ng-container>
        </ng-container>
        <ng-container *ngIf="saving">
          <ng-container *ngIf="mode === 'add'">Creating…</ng-container>
          <ng-container *ngIf="mode === 'edit'">Saving…</ng-container>
        </ng-container>
      </button>
    </div>
  </form>
</div>
